import { useEffect, useState } from 'react';
import { RecordContextProvider, useUpdate, useNotify, ResourceContextProvider, useSidebarState, useListContext } from 'react-admin';
import { Typography, Box, Paper, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import LeadCard from './LeadCard';
import HorizontalScrollContainer from '../../../common/HorizontalScrollContainer';

const boardSx = {
    p: 2,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
        height: 8,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: 4,
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.3)',
        },
    },
};


const getColumns = (themeMode) => (
    [
        {
            id: 'new',
            title: 'New',
            bgColor: themeMode === 'dark' ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)',
        },
        {
            id: 'contacted',
            title: 'Contacted',
            bgColor: themeMode === 'dark' ? 'rgba(156, 39, 176, 0.08)' : 'rgba(156, 39, 176, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)',
        },
        {
            id: 'qualified',
            title: 'Qualified',
            bgColor: themeMode === 'dark' ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)',
        },
        {
            id: 'meeting_scheduled',
            title: 'Meeting scheduled',
            bgColor: themeMode === 'dark' ? 'rgba(63, 81, 181, 0.08)' : 'rgba(63, 81, 181, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(63, 81, 181, 0.3)' : 'rgba(63, 81, 181, 0.2)',
        },
        {
            id: 'proposal_sent',
            title: 'Proposal sent',
            bgColor: themeMode === 'dark' ? 'rgba(0, 188, 212, 0.08)' : 'rgba(0, 188, 212, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(0, 188, 212, 0.3)' : 'rgba(0, 188, 212, 0.2)',
        },
        {
            id: 'converted',
            title: 'Converted',
            bgColor: themeMode === 'dark' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)',
        },

        {
            id: 'nurturing',
            title: 'Nurturing',
            bgColor: themeMode === 'dark' ? 'rgba(103, 58, 183, 0.08)' : 'rgba(103, 58, 183, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(103, 58, 183, 0.3)' : 'rgba(103, 58, 183, 0.2)',
        },
        {
            id: 'on_hold',
            title: 'On hold',
            bgColor: themeMode === 'dark' ? 'rgba(255, 193, 7, 0.08)' : 'rgba(255, 193, 7, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)',
        },
        {
            id: 'unqualified',
            title: 'Unqualified',
            bgColor: themeMode === 'dark' ? 'rgba(121, 85, 72, 0.08)' : 'rgba(121, 85, 72, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(121, 85, 72, 0.3)' : 'rgba(121, 85, 72, 0.2)',
        },
        {
            id: 'lost',
            title: 'Lost',
            bgColor: themeMode === 'dark' ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.08)',
            borderColor: themeMode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)',
        },
    ]
);

const KanbanBoard = () => {
    const theme = useTheme();
    const { data: fetchedLeads, isLoading } = useListContext();
    const [leads, setLeads] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [update] = useUpdate();
    const notify = useNotify();
    const [menuOpen] = useSidebarState();

    useEffect(() => {
        if (fetchedLeads) {
            setLeads(fetchedLeads);
        }
    }, [fetchedLeads]);

    const columns = getColumns(theme.palette.mode);

    const handleDragEnd = async (result) => {
        const { draggableId, source, destination } = result;
        const id = parseInt(draggableId);

        if (!destination || source.droppableId === destination.droppableId) {
            setIsDragging(false);
            return;
        }

        const lead = leads?.find((l) => l.id === id);
        if (!lead) {
            setIsDragging(false);
            return;
        }

        const newStatus = destination.droppableId;

        const updatedLeads = leads.map((l) => l.id === id ? { ...l, status: newStatus } : l);
        setLeads(updatedLeads);

        try {
            await update(
                'leads',
                {
                    id,
                    data: { status: newStatus },
                    previousData: lead,
                },
                {
                    mutationMode: 'optimistic',
                    meta: { invalidateQueries: false }
                },
            );
        } catch (error) {
            notify('Error while moving the card', { type: 'error' });
        }
        setIsDragging(false);
    };

    const handleDragStart = () => {
        setIsDragging(true);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <Paper sx={{ ...boardSx, width: `calc(100vw - ${menuOpen ? '290px' : '100px'})` }}>
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                <HorizontalScrollContainer sx={{ pb: 2 }} disableDragScroll={isDragging}>
                    <Box display='flex' gap={2}>
                        {columns.map((column) => {
                            const columnLeads = leads?.filter((l) => l.status === column.id) || [];

                            return (
                                <Box
                                    key={column.id}
                                    sx={{
                                        minWidth: 280,
                                        flex: 1,
                                        bgcolor: column.bgColor,
                                        borderRadius: 2,
                                        p: 2,
                                        border: `1px solid ${column.borderColor}`,
                                    }}
                                >
                                    <Box display='flex' justifyContent='space-between' alignItems='center' mb={2} >
                                        <Typography variant='h6' fontWeight='bold'>{column.title}</Typography>
                                        <Chip label={columnLeads.length} size='small' color='primary' />
                                    </Box>

                                    <Droppable droppableId={column.id}>
                                        {(provided, snapshot) => (
                                            <Box
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                sx={{
                                                    minHeight: 200,
                                                    bgcolor: snapshot.isDraggingOver
                                                        ? (theme.palette.mode === 'dark'
                                                            ? 'rgba(32, 178, 170, 0.1)'
                                                            : 'rgba(0, 139, 131, 0.08)')
                                                        : 'transparent',
                                                    borderRadius: 1,
                                                    p: 1,
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                {columnLeads.map((lead, index) => (
                                                    <Draggable
                                                        key={lead.id}
                                                        draggableId={String(lead.id)}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <Box
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                sx={{
                                                                    mb: 1,
                                                                    transform: snapshot.isDragging
                                                                        ? 'rotate(3deg)'
                                                                        : 'none',
                                                                }}
                                                            >
                                                                <RecordContextProvider value={lead}>
                                                                    <LeadCard lead={lead} />
                                                                </RecordContextProvider>
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </Box>
                            );
                        })}
                    </Box>
                </HorizontalScrollContainer>
            </DragDropContext>
        </Paper >
    );
};

const LeadKanban = () => (
    <ResourceContextProvider value='leads'>
        <KanbanBoard />
    </ResourceContextProvider>
);

export default LeadKanban;
